import React from 'react'
import * as ROUTES from 'constants/routes'
import { Button, Flex, Icon, Text, Link } from '@workwhile/ui'
import { ColumnDef } from '@tanstack/react-table'
import { ExternalLinkIcon } from 'lucide-react'
import { formatInTimeZone } from 'date-fns-tz'
import { useIndeedFlexContextValue } from '../../context/IndeedFlexProvider'
import { useIndeedFlexShiftIssuesContextValue } from '../context/IndeedFlexShiftIssuesProvider'
import { IndeedFlexShiftIssueActions } from 'pages/IndeedFlex/shared/components/IndeedFlexShiftIssueActions'
import {
  INDEED_FLEX_SHIFT_ISSUE_LABELS,
  isIndeedFlexTimesheetOnlyIssues,
} from 'pages/IndeedFlex/shared/constants/shiftIssues'
import { ViewIndeedFlexTimesheetButton } from 'pages/IndeedFlex/shared/components/ViewIndeedFlexTimesheetButton'
import { buildIndeedFlexJobShiftsUrl } from 'pages/IndeedFlex/shared/utils/jobShifts'
import { PaginatedTable } from 'components/Table/PaginatedTable'

const INDEED_FLEX_AGENCY_BASE_URL = 'https://agency.indeedflex.com'

type IndeedFlexShiftIssuesItem = ReturnType<
  typeof useIndeedFlexShiftIssuesContextValue
>['state']['data']['items'][number]

function buildIndeedFlexJobUrl({
  ifPlatformId,
  ifJobId,
  ifRoleId,
  ifVenueId,
}: {
  ifPlatformId: number
  ifJobId: number
  ifRoleId: number
  ifVenueId: number
}): string {
  const params = new URLSearchParams({
    roleId: ifRoleId.toString(),
    venueId: ifVenueId.toString(),
  })

  return `${INDEED_FLEX_AGENCY_BASE_URL}/platforms/${ifPlatformId}/job-details/${ifJobId}?${params.toString()}`
}

const OpenIndeedFlexJobButton = ({
  ifClientId,
  ifJobId,
  ifRoleId,
  ifVenueId,
  issues,
  startTime,
  endTime,
}: {
  ifClientId: number
  ifJobId: number
  ifRoleId: number
  ifVenueId: number
  issues: IndeedFlexShiftIssuesItem['issues']
  startTime: string
  endTime: string
}) => {
  const {
    state: { clients },
  } = useIndeedFlexContextValue()

  if (isIndeedFlexTimesheetOnlyIssues(issues)) {
    return (
      <ViewIndeedFlexTimesheetButton
        ifJobId={ifJobId}
        startTime={startTime}
        endTime={endTime}
      />
    )
  }

  const ifPlatformId = clients.find(
    (client) => client.ifClientId === ifClientId
  )?.ifPlatformId

  if (ifPlatformId === undefined) {
    return null
  }

  return (
    <Button
      kind="small"
      variant="secondary"
      as="a"
      href={buildIndeedFlexJobUrl({
        ifPlatformId,
        ifJobId,
        ifRoleId,
        ifVenueId,
      })}
      target="_blank"
      rel="noopener noreferrer"
      style={{ padding: '0 0.5rem', textDecoration: 'none' }}
    >
      <Flex alignItems="center" gap={1}>
        <Text fontSize="12px">Open IF job</Text>
        <Icon icon={ExternalLinkIcon} size={12} />
      </Flex>
    </Button>
  )
}

const OpenWorkWhileShiftButton = ({
  workShiftId,
}: {
  workShiftId: number | null
}) => {
  if (workShiftId === null) {
    return null
  }

  return (
    <Button
      kind="small"
      variant="secondary"
      as="a"
      href={ROUTES.SHIFT_DETAIL.replace(':shiftId', workShiftId.toString())}
      target="_blank"
      rel="noopener noreferrer"
      style={{ padding: '0 0.5rem', textDecoration: 'none' }}
    >
      <Flex alignItems="center" gap={1}>
        <Text fontSize="12px">View shift</Text>
        <Icon icon={ExternalLinkIcon} size={12} />
      </Flex>
    </Button>
  )
}

const CompanyCell = ({
  companyId,
  companyName,
}: {
  companyId: number | null
  companyName: string | null
}) => {
  if (companyId === null || companyName === null) {
    return <Text>-</Text>
  }

  return (
    <Text>
      {companyName} (
      <Link
        to={ROUTES.COMPANY_DETAIL.replace(':companyId', companyId.toString())}
        target="_blank"
      >
        {companyId}
      </Link>
      )
    </Text>
  )
}

const columns: ColumnDef<IndeedFlexShiftIssuesItem>[] = [
  {
    header: 'Client',
    accessorKey: 'ifClientId',
    cell: ({ row }) => (
      <Text>
        {row.original.clientName} ({row.original.ifClientId})
      </Text>
    ),
  },
  {
    header: 'Job ID',
    accessorKey: 'ifJobId',
    cell: ({ row }) => (
      <Link
        to={buildIndeedFlexJobShiftsUrl({
          ifJobId: row.original.ifJobId,
          ifShiftId: row.original.ifShiftId,
        })}
        target="_blank"
      >
        {row.original.ifJobId}
      </Link>
    ),
  },
  {
    header: 'Shift ID',
    accessorKey: 'ifShiftId',
    cell: ({ row }) => {
      const { workShiftId, ifShiftId } = row.original

      if (workShiftId === null) {
        return <Text>{ifShiftId}</Text>
      }

      return (
        <Link
          to={ROUTES.SHIFT_DETAIL.replace(':shiftId', workShiftId.toString())}
          target="_blank"
        >
          {workShiftId}
        </Link>
      )
    },
  },
  {
    header: 'Role',
    accessorKey: 'ifRoleId',
    cell: ({ row }) => <Text>{row.original.roleName}</Text>,
  },
  {
    header: 'Venue',
    accessorKey: 'ifVenueId',
    cell: ({ row }) => <Text>{row.original.venueName}</Text>,
  },
  {
    header: 'Start Time',
    accessorKey: 'startTime',
    cell: ({ row }) => {
      const startTime = formatInTimeZone(
        row.original.startTime,
        row.original.timezone,
        'EEEE, MMM d, h:mm a z'
      )
      return <Text>{startTime}</Text>
    },
  },
  {
    header: 'End Time',
    accessorKey: 'endTime',
    cell: ({ row }) => {
      const endTime = formatInTimeZone(
        row.original.endTime,
        row.original.timezone,
        'EEEE, MMM d, h:mm a z'
      )
      return <Text>{endTime}</Text>
    },
  },
  {
    header: 'Company',
    accessorKey: 'companyId',
    cell: ({ row }) => (
      <CompanyCell
        companyId={row.original.companyId}
        companyName={row.original.companyName}
      />
    ),
  },
  {
    header: 'Issues',
    cell: ({ row }) => {
      return row.original.issues.map((issue) => (
        <Text key={issue}>
          {INDEED_FLEX_SHIFT_ISSUE_LABELS[issue] ?? issue}
        </Text>
      ))
    },
  },
  {
    header: 'Actions',
    cell: ({ row }) => (
      <Flex flexDirection="column" gap={1}>
        <OpenIndeedFlexJobButton
          ifClientId={row.original.ifClientId}
          ifJobId={row.original.ifJobId}
          ifRoleId={row.original.ifRoleId}
          ifVenueId={row.original.ifVenueId}
          issues={row.original.issues}
          startTime={row.original.startTime}
          endTime={row.original.endTime}
        />
        <OpenWorkWhileShiftButton workShiftId={row.original.workShiftId} />
        <IndeedFlexShiftIssueActions shift={row.original} />
      </Flex>
    ),
  },
]

export const IndeedFlexShiftIssuesTable = () => {
  const {
    state: { data, pagination, isFetching },
    actions: { setPagination },
  } = useIndeedFlexShiftIssuesContextValue()

  return (
    <PaginatedTable
      columns={columns}
      items={data.items}
      rowCount={data.totalRows}
      pagination={pagination}
      setPagination={setPagination}
      isFetching={isFetching}
      emptyMessage="No shift issues found."
      emptyStateHeight={50}
    />
  )
}
